import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "thorax_token";

export const api = axios.create({
  baseURL: API,
  timeout: 120000,
});

export const getStoredToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
};

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    localStorage.removeItem(TOKEN_KEY);
    delete api.defaults.headers.common["Authorization"];
  }
};

// Initialize token from storage on load
const initial = getStoredToken();
if (initial) {
  api.defaults.headers.common["Authorization"] = `Bearer ${initial}`;
}

// Auto-logout on 401
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      setAuthToken(null);
      // Reload to /login unless we are already there
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

// ---------------- Analysis ----------------
export const analyzeXray = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post("/analyze", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
};

// ---------------- Examinations ----------------
export const createExamination = async (payload) => {
  const { data } = await api.post("/examinations", payload);
  return data;
};

export const listExaminations = async (q = "") => {
  const { data } = await api.get("/examinations", { params: { q } });
  return data;
};

export const getExamination = async (id) => {
  const { data } = await api.get(`/examinations/${id}`);
  return data;
};

export const deleteExamination = async (id) => {
  const { data } = await api.delete(`/examinations/${id}`);
  return data;
};

export const patchExamination = async (id, patch) => {
  const { data } = await api.patch(`/examinations/${id}`, patch);
  return data;
};

export const getStats = async () => {
  const { data } = await api.get("/stats");
  return data;
};

export const verifyExamination = async (id, verification_status, corrections = []) => {
  const { data } = await api.post(`/examinations/${id}/verify`, {
    verification_status,
    corrections,
  });
  return data;
};

export const getPriorExaminations = async (id) => {
  const { data } = await api.get(`/examinations/${id}/prior`);
  return data;
};

export const generateReportDraft = async (id, { compare_with_exam_id = null, persist = false } = {}) => {
  const { data } = await api.post(`/examinations/${id}/report/generate`, {
    compare_with_exam_id,
    persist,
  });
  return data;
};

export const getClasses = async () => {
  const { data } = await api.get("/classes");
  return data.classes;
};

// ---------------- PDF (auth-aware, downloads as blob) ----------------
export const downloadPdf = async (id, { compareWith = null, includeReport = true } = {}) => {
  const params = {};
  if (compareWith) params.compare_with = compareWith;
  if (!includeReport) params.include_report = false;
  const resp = await api.get(`/examinations/${id}/pdf`, {
    responseType: "blob",
    params,
  });
  const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `MCU_Xray_${id.slice(0, 8)}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ---------------- Exports (admin-only) ----------------
export const downloadExportFile = async (endpoint, filename) => {
  const resp = await api.get(endpoint, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([resp.data]));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// ---------------- ILO classification ----------------
export const getIloOptions = async () => {
  const { data } = await api.get("/ilo/options");
  return data;
};

export const saveIloClassification = async (id, payload) => {
  const { data } = await api.put(`/examinations/${id}/ilo`, payload);
  return data;
};

export const clearIloClassification = async (id) => {
  const { data } = await api.delete(`/examinations/${id}/ilo`);
  return data;
};

// ---------------- Classes (auto-discovered class labels) ----------------
export const listClasses = async () => {
  const { data } = await api.get("/classes");
  return data.classes || [];
};

export const addClass = async (name, color = null) => {
  const { data } = await api.post("/classes", { name, color });
  return data;
};

export const updateClass = async (currentName, { name, color } = {}) => {
  const { data } = await api.patch(`/classes/${encodeURIComponent(currentName)}`, {
    name: name || currentName,
    color,
  });
  return data;
};

export const deleteClass = async (name, force = false) => {
  const { data } = await api.delete(`/classes/${encodeURIComponent(name)}`, {
    params: { force },
  });
  return data;
};

// ---------------- Admin ----------------
export const startRoboflowPush = async ({ only_reviewed = true, only_unpushed = true, batch_name = "", split = "train" } = {}) => {
  const { data } = await api.post("/export/roboflow-push", {
    only_reviewed,
    only_unpushed,
    batch_name,
    split,
  });
  return data;
};

export const listRoboflowPushes = async () => {
  const { data } = await api.get("/export/roboflow-push");
  return data;
};

export const getRoboflowPushSummary = async (jobId) => {
  const { data } = await api.get(`/export/roboflow-push/${jobId}/summary`);
  return data;
};

export const getRoboflowPush = async (jobId) => {
  const { data } = await api.get(`/export/roboflow-push/${jobId}`);
  return data;
};

export const adminListUsers = async () => {
  const { data } = await api.get("/admin/users");
  return data;
};

export const adminCreateUser = async (payload) => {
  const { data } = await api.post("/admin/users", payload);
  return data;
};

export const adminUpdateUser = async (id, patch) => {
  const { data } = await api.patch(`/admin/users/${id}`, patch);
  return data;
};

export const adminDeleteUser = async (id) => {
  const { data } = await api.delete(`/admin/users/${id}`);
  return data;
};
